import catalog from "./workspaceCatalog.json";

export { catalog };
export const human = (value) => String(value).replace(/_/g, " ").replace(/\bId\b/gi, "reference").replace(/^./, (c) => c.toUpperCase());
export const references = {
  entity_id: ["entities", "entity_id"], parent_entity_id: ["entities", "entity_id"],
  legal_owner_entity_id: ["entities", "entity_id"], paying_entity_id: ["entities", "entity_id"],
  employing_entity_id: ["entities", "entity_id"], related_entity_id: ["entities", "entity_id"],
  account_id: ["accounts", "account_id"], debit_account_id: ["accounts", "account_id"],
  credit_account_id: ["accounts", "account_id"], account_code: ["accounts", "code"],
  counterparty_id: ["counterparties", "counterparty_id"], vendor_counterparty_id: ["counterparties", "counterparty_id"],
  customer_counterparty_id: ["counterparties", "counterparty_id"], bank_account_id: ["bank_accounts", "bank_account_id"],
  fiscal_period_id: ["periods", "period_id"], period_id: ["periods", "period_id"],
  journal_entry_id: ["journals", "journal_entry_id"], ledger_journal_id: ["journals", "journal_entry_id"],
  bill_id: ["bills", "bill_id"], invoice_id: ["invoices", "invoice_id"], payment_id: ["payments", "payment_id"],
  receipt_payment_id: ["payments", "payment_id"], credit_id: ["credits", "credit_id"],
  budget_id: ["budgets", "budget_id"], budget_ref: ["budgets", "budget_id"], forecast_id: ["forecasts", "forecast_id"],
  request_id: ["purchase_requests", "request_id"], po_id: ["purchase_orders", "po_id"],
  agreement_id: ["agreements", "agreement_id"], order_id: ["orders", "order_id"],
  fulfillment_id: ["fulfillments", "fulfillment_id"], obligation_id: ["obligations", "obligation_id"],
  project_id: ["capital_projects", "project_id"], asset_id: ["assets", "asset_id"],
  jurisdiction_id: ["tax_jurisdictions", "jurisdiction_id"], cycle_id: ["close_cycles", "cycle_id"],
  canonical_entity_id: ["canonical_entities", "canonical_entity_id"], unit_key: ["units", "unit_key"],
};
export function recordLabel(row, primary) {
  const name = row.legal_name || row.display_name || row.title || row.name || row.feature || row.vendor || row.label ||
    row.description || row.masked_label || row.invoice_number || row.vendor_reference || row.code ||
    (row.start_date && row.end_date ? `${row.start_date} – ${row.end_date}` : null) || row[primary];
  const state = row.state || row.lifecycle_state || row.status;
  return `${name || "Record"}${state ? ` · ${human(state)}` : ""}`;
}

export function defaults(schema = {}) {
  if (schema.default != null) return structuredCopy(schema.default);
  if (schema.type === "object") return Object.fromEntries(Object.entries(schema.properties || {}).map(([key, spec]) => [key, defaults(spec)]));
  if (schema.type === "array") return Array.from({length: schema.minItems || 0}, () => defaults(schema.items));
  if (schema.type === "boolean") return false;
  return "";
}
const structuredCopy = (value) => JSON.parse(JSON.stringify(value));

export function payloadValue(schema = {}, value) {
  if (schema.type === "object" || schema.properties) {
    return Object.fromEntries(Object.entries(schema.properties || {}).flatMap(([key, spec]) => {
      const v = value?.[key];
      if (v == null || v === "") return [];
      if (spec.type === "array" && !v.length && !(schema.required || []).includes(key)) return [];
      return [[key, payloadValue(spec, v)]];
    }));
  }
  if (schema.type === "array") return (value || []).map((v) => payloadValue(schema.items, v));
  if (schema.type === "integer" || schema.type === "number") {
    const n = Number(value);
    if (!Number.isFinite(n) || (schema.type === "integer" && !Number.isInteger(n))) throw new Error("Enter a valid number.");
    return n;
  }
  return value;
}

export function parameterValue(name, row, table, entityId) {
  if (name === "entity_id") return entityId || "";
  if (row?.[name] != null) return row[name];
  const aliases = {jid:"journal_entry_id", bid:"bill_id", iid:"invoice_id", oid:"order_id", fid:"fulfillment_id", eid:"exc_id", tid:"bank_txn_id"};
  if (row && aliases[name] && row[aliases[name]]) return row[aliases[name]];
  if (name === "cid" && table?.group === "close") return row?.cycle_id || "";
  if (["rid","pid","mid","aid","cid","doc_id"].includes(name)) return row?.[table?.primary] || "";
  return "";
}

export function prepareAction(action, table, row, entityId) {
  const schema = structuredCopy(action.schema || {type:"object",properties:{}});
  const state = row?.state || row?.lifecycle_state || row?.status;
  if (action.path.endsWith("/transition") && table?.transitions && state) {
    const key = schema.properties?.target_state ? "target_state" : "to_state";
    if (schema.properties?.[key]) schema.properties[key].enum = table.transitions[state] || [];
  }
  const body = defaults(schema);
  for (const [key, spec] of Object.entries(schema.properties || {})) {
    if (["entity_id","paying_entity_id","employing_entity_id"].includes(key) && entityId) body[key] = entityId;
    if (key === "idempotency_key") body[key] = `founder-${window.crypto.randomUUID()}`;
    if (row && (key.endsWith("_id") || key.endsWith("_key")) && row[key] != null) body[key] = row[key];
    if (action.method === "put" && row?.[key] != null) body[key] = structuredCopy(row[key]);
    if (spec.type === "array" && !body[key]?.length && key === "lines") body[key] = [defaults(spec.items)];
  }
  if (body.lines) body.lines = body.lines.map((line, i) => ({...line, ...(schema.properties.lines.items.properties?.line_no ? {line_no:i+1} : {})}));
  const params = Object.fromEntries(action.params.map((p) => [p.name, parameterValue(p.name,row,table,entityId) || defaults(p.schema)]));
  return {schema, body, params};
}

export function actionRequest(action, schema, body, params) {
  let url = action.path;
  const query = {};
  for (const param of action.params) {
    const value = params[param.name];
    if ((value == null || value === "") && param.required) throw new Error(`${human(param.name)} is required.`);
    if (param.location === "path") url = url.replace(`{${param.name}}`, encodeURIComponent(value));
    else if (value != null && value !== "") query[param.name] = payloadValue(param.schema,value);
  }
  if (url.includes("{")) throw new Error("Choose the record for this action.");
  return {method:action.method, url, params:query, ...(action.readOnly ? {} : {data:payloadValue(schema,body)})};
}

export function validationErrors(schema, value, prefix="") {
  const errors=[];
  if (schema.type === "object" || schema.properties) {
    for (const key of schema.required || []) if (value?.[key] == null || value[key] === "") errors.push(`${prefix}${human(key)} is required.`);
    for (const [key,spec] of Object.entries(schema.properties || {})) if (value?.[key] != null && value[key] !== "") errors.push(...validationErrors(spec,value[key],`${prefix}${human(key)}: `));
  } else if (schema.type === "array") {
    if ((value || []).length < (schema.minItems || 0)) errors.push(`${prefix}add at least ${schema.minItems} rows.`);
    (value || []).forEach((v,i) => errors.push(...validationErrors(schema.items,v,`${prefix}row ${i+1}, `)));
  } else {
    if (schema.enum && !schema.enum.includes(value)) errors.push(`${prefix}choose an allowed value.`);
    if (schema.minLength && String(value).length < schema.minLength) errors.push(`${prefix}use at least ${schema.minLength} characters.`);
    if (schema.maxLength && String(value).length > schema.maxLength) errors.push(`${prefix}use at most ${schema.maxLength} characters.`);
    if (["number","integer"].includes(schema.type)) {
      const n=Number(value);
      if (!Number.isFinite(n) || (schema.type === "integer" && !Number.isInteger(n)) || (schema.minimum != null && n<schema.minimum) || (schema.maximum != null && n>schema.maximum)) errors.push(`${prefix}enter a valid number.`);
    }
  }
  return errors;
}
